import { ArrowUpRight, Bot, Inbox, Info } from 'lucide-react';
import pluralize from 'pluralize';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type { CandidateJobCountsApi } from '@/modules/candidates/types';
import { JobStepsContent } from '@/modules/hiring-manager/components/JobStepsContent';
import { ConnectLinkedInLink } from '@/modules/hiring-manager/linkedin/ConnectLinkedInLink';
import {
  type ActivityStep,
  getJobActivity,
} from '@/modules/hiring-manager/utils/jobActivity';
import { outreachHold } from '@/modules/hiring-manager/utils/outreachReadiness';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Tooltip,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import type { JobAutomation } from '@/pages/hm/job-automation/jobAutomation';

import type { CareersPage } from './useCareersPage';

interface AgentBarProps {
  job: JobApi;
  /** What the plan actually runs, not only what the job's flags ask for. */
  automation: JobAutomation;
  integrationsHref: string;
  /** Drafts Finlay wrote that wait for the user's review. */
  drafts: number;
  /** People Finlay introduced who wait on the user's call; undefined when not shown. */
  decisions: number | undefined;
  onReviewDrafts: () => void;
  onReviewDecisions: () => void;
  /** Shown on the Finlay line while the company's Careers Page is on. */
  careersPage: CareersPage | undefined;
  /** New Applicants in a fixed window of days; undefined until loaded. */
  newApplicants: CandidateJobCountsApi['newApplicants'] | undefined;
  /** Undefined while the list already shows only Inbound candidates. */
  onShowInbound: (() => void) | undefined;
}

interface Handoff {
  tone: 'blocked' | 'waiting';
  text: string;
  action: React.ReactNode;
}

/**
 * The job's Finlay section between its title and tabs: the steps Finlay is
 * running, every step on "How this job runs", and one line per thing it
 * has handed back to the user, most urgent first.
 */
export function AgentBar({
  job,
  automation,
  integrationsHref,
  drafts,
  decisions,
  onReviewDrafts,
  onReviewDecisions,
  careersPage,
  newApplicants,
  onShowInbound,
}: AgentBarProps) {
  const { linkedInStatus } = useIsLinkedInConnected();
  const hold = outreachHold(job, linkedInStatus);
  const activity = getJobActivity({ job, automation, outreachHold: hold });
  const running = activity.steps.filter((step) => step.state === 'running');
  // A failed search has its own state and Retry in the job header.
  const isOutreachBlocked = activity.steps.some(
    (step) => step.name === 'Outreach' && step.state === 'blocked',
  );
  const handoffs: Handoff[] = [];
  if (isOutreachBlocked && hold) {
    handoffs.push({
      tone: hold.tone,
      text: hold.text,
      action: (
        <ConnectLinkedInLink
          reconnect={hold.reconnect}
          to={integrationsHref}
          variant="button"
        />
      ),
    });
  }
  if (drafts > 0) {
    handoffs.push({
      tone: 'waiting',
      text: `Finlay drafted ${pluralize('outreach message', drafts, true)}. Nothing is sent until you review ${drafts === 1 ? 'it' : 'them'}.`,
      action: (
        <Button
          className="h-8 rounded-md"
          onClick={onReviewDrafts}
          size="sm"
          variant="outline"
        >
          Review & send
        </Button>
      ),
    });
  }
  if (decisions) {
    handoffs.push({
      tone: 'waiting',
      text: `${pluralize('candidate', decisions, true)} Finlay introduced ${decisions === 1 ? 'is' : 'are'} waiting for your decision.`,
      action: (
        <Button
          className="h-8 rounded-md"
          onClick={onReviewDecisions}
          size="sm"
          variant="outline"
        >
          Review
        </Button>
      ),
    });
  }
  // Last: news, not work waiting on the user.
  if (newApplicants && newApplicants.count > 0) {
    handoffs.push({
      tone: 'waiting',
      text: `${pluralize('person', newApplicants.count, true)} applied through your careers page in the last ${newApplicants.windowDays} days.`,
      action: onShowInbound && (
        <Button
          className="h-8 rounded-md"
          onClick={onShowInbound}
          size="sm"
          variant="outline"
        >
          Show inbound
        </Button>
      ),
    });
  }

  return (
    <section aria-labelledby="finlay-heading" className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b pb-2.5">
        <Bot aria-hidden className="size-3.5" />
        <h2 className="text-[13px] font-semibold" id="finlay-heading">
          Finlay
        </h2>
        <span className="relative grid size-3 shrink-0 place-items-center">
          {running.length > 0 && (
            <span className="bg-secondary-150 absolute inset-0 animate-[portal-live_1.6s_ease-in-out_infinite] rounded-full motion-reduce:animate-none" />
          )}
          <span
            className={cn(
              'relative size-1.5 rounded-full',
              running.length > 0 ? 'bg-primary' : 'bg-neutral-400',
            )}
          />
        </span>
        {running.length > 0 ? (
          <ul aria-label="Running now" className="flex flex-wrap gap-1.5">
            {running.map((step) => (
              <li
                className="bg-muted rounded-full px-2 py-0.5 text-xs font-medium"
                key={step.name}
              >
                {step.name}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-neutral-500">
            {activity.summary?.text ?? 'Not running'}
          </span>
        )}
        {careersPage?.isEnabled && (
          <CareersPageStatus
            isOpen={careersPage.isOpen}
            url={careersPage.url}
          />
        )}
        <HowThisJobRuns steps={activity.steps} />
      </div>
      {/* Handoffs arrive as their queries load: fade in rather than pop. */}
      {handoffs.map((handoff) => (
        <div
          className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b py-2.5 text-[13px] transition-opacity duration-200 starting:opacity-0"
          key={handoff.text}
        >
          <span
            aria-hidden
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              handoff.tone === 'blocked' ? 'bg-red-500' : 'bg-amber-500',
            )}
          />
          <p className="min-w-[min(100%,16rem)] flex-1">{handoff.text}</p>
          <div className="ml-auto flex shrink-0">{handoff.action}</div>
        </div>
      ))}
    </section>
  );
}

/**
 * Whether people can apply to this job, with a link to the company's careers
 * page: there is no public page per job.
 */
function CareersPageStatus({
  isOpen,
  url,
}: {
  isOpen: boolean | undefined;
  url: string;
}) {
  return (
    <span className="flex items-center gap-2 border-l pl-3 text-xs">
      <Inbox aria-hidden className="size-3.5" />
      <span className="text-[13px] font-semibold">Careers page</span>
      {isOpen !== undefined && (
        <span className="flex items-center gap-1.5 text-neutral-500">
          <span
            aria-hidden
            className={cn(
              'size-1.5 rounded-full',
              isOpen ? 'bg-green-600' : 'bg-neutral-400',
            )}
          />
          {isOpen ? 'Open' : 'Closed'}
        </span>
      )}
      <a
        className="text-primary focus-visible:ring-ring/50 inline-flex items-center gap-0.5 rounded-sm font-medium outline-none hover:underline focus-visible:ring-[3px]"
        href={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        View
        <ArrowUpRight aria-hidden className="size-3" />
        <span className="sr-only"> careers page (opens in a new tab)</span>
      </a>
    </span>
  );
}

/**
 * Every step and who handles it. A tap opens it too: a tooltip alone never
 * opens on touch.
 */
function HowThisJobRuns({ steps }: { steps: ActivityStep[] }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Tooltip onOpenChange={setIsOpen} open={isOpen}>
      <TooltipTrigger asChild>
        <button
          className="hover:text-foreground focus-visible:ring-ring/50 ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-sm text-xs text-neutral-500 outline-none focus-visible:ring-[3px]"
          // Radix closes a tooltip on click; preventing that lets a tap open
          // it. A tap outside closes it again.
          onClick={(event) => {
            event.preventDefault();
            setIsOpen(true);
          }}
          type="button"
        >
          How this job runs
          <Info aria-hidden className="size-3.5 text-neutral-400" />
        </button>
      </TooltipTrigger>
      <JobStepsContent steps={steps} title="How this job runs" />
    </Tooltip>
  );
}
